import React, { ChangeEvent, Dispatch, SetStateAction, useState } from 'react';
import { useParams } from 'react-router-dom';

import { css } from '@emotion/react';
import {
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import { toast } from 'react-toastify';

import Button from '@components/Common/Button';
import Checkbox from '@components/Common/Checkbox';
import DatePicker from '@components/Common/DatePicker';
import Form from '@components/Common/Form';
import FormRow from '@components/Common/FormRow';
import FormSection from '@components/Common/FormSection';
import Input from '@components/Common/Input';
import LoadingSpinner from '@components/Common/LoadingSpinner';
import SelectBox from '@components/Common/SelectBox';
import TextArea from '@components/Common/TextArea';
import ConfirmModal from '@components/Modals/ConfirmModal';
import CompanySearchModal from '@components/Modals/Portfolio/CompanySearchModal';

import {
  PortfolioBasicInfoCompanyFormInputs,
  PortfolioCreateFormInputs,
} from '@type/portfolio';

import { useGroupDetail } from '@hooks/api/group/useGroupAPIs';
import { usePortfolioCreate } from '@hooks/api/portfolio/usePortfolioAPIs';
import { useInternalRouter } from '@hooks/useInternalRouter';

import { titleStyle } from '@styles/common';
import { palette } from '@styles/theme';

import { STOCK_TYPES } from '@constants/commons';
import { DELETE_SUCCESS_MESSAGE } from '@constants/toastMessage';
import {
  MAX_AMOUNT,
  emailValidateOptions,
  nameOptionalValidateOptions,
  newAmountValidateOptions,
  phoneOptionalValidateOptions,
} from '@constants/validation';

import { bizNumberFormat, comma } from '@utils/utils';

/** 조합 관리 > 상세 > 포트폴리오 등록 */
const PortfolioNew = () => {
  const router = useInternalRouter();
  const { groupId } = useParams();
  const { data: groupData } = useGroupDetail();

  /** 기업 검색 모달 */
  const [searchCompanyModalOpen, setSearchCompanyModalOpen] =
    useState<boolean>(false);

  /** 등록하기 Confirm 모달 */
  const [confirmModalOpen, setConfirmModalOpen] = useState<boolean>(false);

  /** 담당자 삭제용 ID */
  const [assigneeDeleteId, setAssigneeDeleteId] = useState<number>();
  const [assigneeDeleteModalOpen, setAssigneeDeleteModalOpen] =
    useState<boolean>(false);

  /** 투자정보 삭제용 ID */
  const [investDeleteId, setInvestDeleteId] = useState<number>();
  const [investDeleteModalOpen, setInvestDeleteModalOpen] =
    useState<boolean>(false);

  /** 기업 정보 저장 */
  const [companyInfo, setCompanyInfo] =
    useState<PortfolioBasicInfoCompanyFormInputs>();

  /** 기업 검색 모달에서 검색결과의 특정 Row를 선택했을 때  */
  const handleSelect = (value: PortfolioBasicInfoCompanyFormInputs) => {
    setCompanyInfo(value);
  };

  /** react-hook-form Init */
  const {
    control,
    getValues,
    formState: { errors },
    handleSubmit,
    watch,
  } = useForm<PortfolioCreateFormInputs>({
    defaultValues: {
      assigneeInfos: [
        {
          email: '',
          name: '',
          phone: '',
        },
      ],
      investInfos: [
        {
          faceValue: '',
          totalShares: '',
          investmentUnitPrice: '',
          shareholding: '',
          investmentAmount: '',
          corporateValue: '',
          stockType: '',
          primaryInvestments: [],
          requiredInvestment: false,
          reportMatter: undefined,
          consentMatter: undefined,
        },
      ],
    },
  });

  const {
    fields: emailFields,
    append: emailAppend,
    remove: emailRemove,
  } = useFieldArray({
    control: control,
    name: 'assigneeInfos',
  });

  const {
    fields: investFields,
    append: investAppend,
    remove: investRemove,
  } = useFieldArray({
    control: control,
    name: 'investInfos',
  });

  /** 금액 콤마 */
  const handleOnChangeNumber = (
    e: ChangeEvent<HTMLInputElement>,
    setValue: Dispatch<SetStateAction<string>>,
    fieldOnChange: any
  ) => {
    /** 숫자만 입력값으로 넘김 */
    const validInputValue = e.target.value
      .replaceAll(',', '')
      .replace(/[^0-9]/g, '')
      .replace(/^0+/, '');

    setValue(validInputValue === '' ? '' : comma(validInputValue));
    fieldOnChange(validInputValue === '' ? '' : comma(validInputValue));
  };

  /** 기업 등록 API */
  const { mutate, isLoading } = usePortfolioCreate(
    getValues('assigneeInfos'),
    getValues('investInfos'),
    setConfirmModalOpen
  );

  const onConfirm: SubmitHandler<PortfolioCreateFormInputs> = async () => {
    try {
      setConfirmModalOpen(true);
    } catch (error: any) {
      console.log('PORTFOLIO_CREATE_CONFIRM_ERROR');
    }
  };

  const onSubmit: SubmitHandler<PortfolioCreateFormInputs> = async ({
    assigneeInfos,
    investInfos,
  }) => {
    try {
      mutate({ mappingId: companyInfo?.mappingId || '' });
    } catch (error: any) {
      console.log('PORTFOLIO_CREATE_SUBMIT_ERROR');
    }
  };

  return (
    <>
      <div css={[titleStyle, { justifyContent: 'space-between' }]}>
        포트폴리오사 등록
        <div css={buttonWrap}>
          <Button
            label="목록으로"
            theme="grayscale"
            handleClick={() => router.push(`/group/${groupId}`)}
          />
          <Button
            label="등록하기"
            theme="secondary"
            handleClick={handleSubmit(onConfirm)}
          />
        </div>
      </div>

      <div css={subTitle}>기업정보</div>
      <form onSubmit={handleSubmit(onConfirm)}>
        <Form>
          <FormRow>
            <FormSection label="기업명" required>
              <div css={{ display: 'flex', gap: '10px' }}>
                {companyInfo && (
                  <input
                    css={[inputStyle, { width: '200px' }]}
                    type="text"
                    value={companyInfo?.name || '-'}
                    readOnly
                  />
                )}
                <Button
                  label="검색"
                  theme="secondary"
                  handleClick={() => setSearchCompanyModalOpen(true)}
                />
              </div>
            </FormSection>
            <FormSection label="대표자명">
              <input
                css={inputStyle}
                type="text"
                value={companyInfo?.ceo || '-'}
                readOnly
              />
            </FormSection>
          </FormRow>
          <FormRow>
            <FormSection label="서비스명">
              <input
                css={inputStyle}
                type="text"
                value={companyInfo?.serviceName || '-'}
                readOnly
              />
            </FormSection>
            <FormSection label="사업자번호">
              <input
                css={inputStyle}
                type="text"
                value={bizNumberFormat(String(companyInfo?.bizNo)) || '-'}
                readOnly
              />
            </FormSection>
          </FormRow>
          <FormRow>
            <FormSection label="자본금">
              <input
                css={inputStyle}
                type="text"
                value={comma(companyInfo?.capital || '-')}
                readOnly
              />
            </FormSection>
            <FormSection label="법인설립일">
              <input
                css={inputStyle}
                type="text"
                value={companyInfo?.establishmentDate || '-'}
                readOnly
              />
            </FormSection>
          </FormRow>
        </Form>

        <div css={[subTitle, { marginTop: '60px' }]}>
          담당자정보
          <Button
            label="담당자추가"
            theme="grayscale"
            variant="line"
            handleClick={() => emailAppend({ email: '' })}
          />
        </div>
        <Form>
          {emailFields.map((d: any, i: number) => (
            <FormRow key={d.id}>
              <FormSection label={`담당자 ${i + 1}`} required>
                <div css={emailInputs}>
                  <div>
                    <Input
                      name={`assigneeInfos.${i}.email`}
                      fieldName={'assigneeInfos'}
                      width={360}
                      control={control}
                      errors={errors}
                      rules={emailValidateOptions}
                      placeHolder={'이메일 주소를 입력해 주세요.'}
                      isErrorText={Boolean(errors.assigneeInfos?.[i]?.email)}
                    />
                    <Input
                      name={`assigneeInfos.${i}.name`}
                      fieldName={'assigneeInfos'}
                      width={360}
                      control={control}
                      errors={errors}
                      rules={nameOptionalValidateOptions}
                      placeHolder={'이름을 입력해 주세요. (선택)'}
                      isErrorText={Boolean(errors.assigneeInfos?.[i]?.name)}
                    />
                    <Input
                      name={`assigneeInfos.${i}.phone`}
                      fieldName={'assigneeInfos'}
                      width={360}
                      control={control}
                      errors={errors}
                      rules={phoneOptionalValidateOptions}
                      placeHolder={'전화번호를 입력해 주세요. (선택)'}
                      isErrorText={Boolean(errors.assigneeInfos?.[i]?.phone)}
                      maxLength={9999}
                    />
                  </div>
                  {i !== 0 && (
                    <Button
                      label="삭제"
                      width={46}
                      height={29}
                      fontSize={14}
                      handleClick={() => {
                        setAssigneeDeleteId(i);
                        setAssigneeDeleteModalOpen(true);
                      }}
                    />
                  )}
                </div>
              </FormSection>
            </FormRow>
          ))}
        </Form>

        <div css={[subTitle, { marginTop: '60px' }]}>
          투자정보
          <Button
            label="투자정보추가"
            theme="grayscale"
            variant="line"
            handleClick={() =>
              investAppend({
                faceValue: '',
                totalShares: '',
                investmentUnitPrice: '',
                shareholding: '',
                investmentAmount: '',
                corporateValue: '',
                stockType: '',
                primaryInvestments:
                  groupData?.primaryInvestments?.map((pi: any) => ({
                    fundPrimaryInvestmentId: pi.id,
                    applicable: false,
                  })) ?? [],
                requiredInvestment: false,
                reportMatter: undefined,
                consentMatter: undefined,
              })
            }
          />
        </div>
        {investFields?.map((d: any, i: number) => (
          <React.Fragment key={d.id}>
            {i !== 0 && (
              <div css={investButton}>
                <Button
                  label="삭제하기"
                  handleClick={() => {
                    setInvestDeleteId(i);
                    setInvestDeleteModalOpen(true);
                  }}
                />
              </div>
            )}
            <Form>
              <FormRow>
                <FormSection label="액면가" required>
                  <Input
                    name={`investInfos.${i}.faceValue`}
                    fieldName={'investInfos'}
                    control={control}
                    errors={errors}
                    width={590}
                    rules={newAmountValidateOptions(
                      '100',
                      '5000',
                      '액면가를 입력해 주세요.'
                    )}
                    placeHolder={'숫자만 입력해 주세요.'}
                    handleChange={handleOnChangeNumber}
                  />
                </FormSection>
                <FormSection label="총 발행주식수" required>
                  <Input
                    name={`investInfos.${i}.totalShares`}
                    fieldName={'investInfos'}
                    control={control}
                    errors={errors}
                    width={590}
                    rules={newAmountValidateOptions(
                      '100',
                      MAX_AMOUNT,
                      '총 발행주식수를 입력해 주세요.'
                    )}
                    placeHolder={'숫자만 입력해 주세요.'}
                    handleChange={handleOnChangeNumber}
                  />
                </FormSection>
              </FormRow>
              <FormRow>
                <FormSection label="투자일자(납일일)" required>
                  <DatePicker
                    name={`investInfos.${i}.investDate`}
                    fieldName={'investInfos'}
                    control={control}
                    errors={errors}
                    rules={{ required: '투자일자를 입력해 주세요.' }}
                  />
                </FormSection>
                <FormSection label="투자단가">
                  <Input
                    name={`investInfos.${i}.investmentUnitPrice`}
                    fieldName={'investInfos'}
                    control={control}
                    errors={errors}
                    width={590}
                    rules={newAmountValidateOptions('0', MAX_AMOUNT)}
                    placeHolder={'숫자만 입력해 주세요.'}
                    handleChange={handleOnChangeNumber}
                  />
                </FormSection>
              </FormRow>
              <FormRow>
                <FormSection label="보유주식수">
                  <Input
                    name={`investInfos.${i}.shareholding`}
                    fieldName={'investInfos'}
                    control={control}
                    errors={errors}
                    width={590}
                    rules={newAmountValidateOptions('0', MAX_AMOUNT)}
                    placeHolder={'숫자만 입력해 주세요.'}
                    handleChange={handleOnChangeNumber}
                  />
                </FormSection>
                <FormSection label="투자금액" required>
                  <Input
                    name={`investInfos.${i}.investmentAmount`}
                    fieldName={'investInfos'}
                    control={control}
                    errors={errors}
                    width={590}
                    rules={newAmountValidateOptions(
                      '100',
                      MAX_AMOUNT,
                      '투자금액을 입력해 주세요.'
                    )}
                    placeHolder={'숫자만 입력해 주세요.'}
                    handleChange={handleOnChangeNumber}
                  />
                </FormSection>
              </FormRow>
              <FormRow>
                <FormSection label="주식유형" required>
                  <SelectBox
                    options={STOCK_TYPES}
                    width={240}
                    name={`investInfos.${i}.stockType`}
                    fieldName="investInfos"
                    control={control}
                    errors={errors}
                    rules={{ required: '주식유형을 선택해 주세요.' }}
                    placeholder="주식유형을 선택해 주세요."
                    isMenuFixed
                  />
                </FormSection>
                <FormSection label="투자기업가치" required>
                  <Input
                    name={`investInfos.${i}.corporateValue`}
                    fieldName={'investInfos'}
                    control={control}
                    errors={errors}
                    width={590}
                    rules={newAmountValidateOptions(
                      '0',
                      MAX_AMOUNT,
                      '투자기업가치를 입력해 주세요.'
                    )}
                    placeHolder={'숫자만 입력해 주세요.'}
                    handleChange={handleOnChangeNumber}
                  />
                </FormSection>
              </FormRow>

              {/* 주목적투자기준 - 여러 건 */}
              {[...(groupData?.primaryInvestments ?? [])]
                .sort((a: any, b: any) => a.priority - b.priority)
                .map((pi: any, piIndex: number) => (
                  <FormRow key={`primary-row-${pi.priority}-${i}`}>
                    <FormSection label={`주목적투자기준 ${pi.priority}`}>
                      <div css={textCell}>{pi.criterion}</div>
                    </FormSection>
                    <FormSection label={`주목적투자 ${pi.priority}`} required>
                      <div css={checkboxGroup}>
                        <Controller
                          name={
                            `investInfos.${i}.primaryInvestments.${piIndex}.applicable` as any
                          }
                          control={control}
                          render={({ field }) => (
                            <>
                              <Checkbox
                                label="해당"
                                checked={Boolean(field.value) === true}
                                value={`primary-${pi.priority}-${i}`}
                                handleChecked={() => field.onChange(true)}
                              />
                              <Checkbox
                                label="미해당"
                                checked={Boolean(field.value) === false}
                                value={`notPrimary-${pi.priority}-${i}`}
                                handleChecked={() => field.onChange(false)}
                              />
                            </>
                          )}
                        />
                      </div>
                    </FormSection>
                  </FormRow>
                ))}

              {/* 의무투자기준 */}
              <FormRow>
                <FormSection label="의무투자기준">
                  <div css={textCell}>{groupData?.requiredInvestment}</div>
                </FormSection>
                <FormSection label="의무투자" required>
                  <div css={checkboxGroup}>
                    <Controller
                      name={`investInfos.${i}.requiredInvestment`}
                      control={control}
                      render={({ field }) => (
                        <>
                          <Checkbox
                            label="해당"
                            checked={field.value === true}
                            value={`required-${i}`}
                            handleChecked={() => field.onChange(true)}
                          />
                          <Checkbox
                            label="미해당"
                            checked={field.value === false}
                            value={`notRequired-${i}`}
                            handleChecked={() => field.onChange(false)}
                          />
                        </>
                      )}
                    />
                  </div>
                </FormSection>
              </FormRow>

              <FormRow>
                <FormSection label="보고사항">
                  <TextArea
                    name={`investInfos.${i}.reportMatter`}
                    fieldName={'investInfos'}
                    control={control}
                    errors={errors}
                    rules={{}}
                    placeHolder={'보고사항을 입력해 주세요.'}
                  />
                </FormSection>
                <FormSection label="동의사항">
                  <TextArea
                    name={`investInfos.${i}.consentMatter`}
                    control={control}
                    rules={{}}
                    placeHolder={'동의사항을 입력해 주세요.'}
                  />
                </FormSection>
              </FormRow>
            </Form>
          </React.Fragment>
        ))}
      </form>

      {/* 기업검색 모달 */}
      {searchCompanyModalOpen && (
        <CompanySearchModal
          open={searchCompanyModalOpen}
          handleOpen={() => setSearchCompanyModalOpen(false)}
          handleSelect={handleSelect}
        />
      )}

      {/* 등록하기 Confirm 모달 */}
      {confirmModalOpen && (
        <ConfirmModal
          title="등록하시겠습니까?"
          open={confirmModalOpen}
          handleOpen={() => setConfirmModalOpen(false)}
          handleCancel={() => setConfirmModalOpen(false)}
          handleConfirm={handleSubmit(onSubmit)}
        />
      )}

      {/* 담당자정보 삭제 모달 */}
      {assigneeDeleteModalOpen && (
        <ConfirmModal
          title="삭제하시겠습니까?"
          buttonText={['취소하기', '삭제하기']}
          open={assigneeDeleteModalOpen}
          handleOpen={() => setAssigneeDeleteModalOpen(false)}
          handleCancel={() => setAssigneeDeleteModalOpen(false)}
          handleConfirm={() => {
            emailRemove(assigneeDeleteId);
            setAssigneeDeleteModalOpen(false);
            toast.success(DELETE_SUCCESS_MESSAGE);
          }}
        />
      )}

      {/* 투자정보 삭제 모달 */}
      {investDeleteModalOpen && (
        <ConfirmModal
          title="삭제하시겠습니까?"
          buttonText={['취소하기', '삭제하기']}
          open={investDeleteModalOpen}
          handleOpen={() => setInvestDeleteModalOpen(false)}
          handleCancel={() => setInvestDeleteModalOpen(false)}
          handleConfirm={() => {
            investRemove(investDeleteId);
            setInvestDeleteModalOpen(false);
            toast.success(DELETE_SUCCESS_MESSAGE);
          }}
        />
      )}

      {isLoading && <LoadingSpinner />}
    </>
  );
};

export default PortfolioNew;

const buttonWrap = css`
  display: flex;
  gap: 10px;
`;

const subTitle = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  font-size: 16px;
  font-weight: 700;
`;

const textCell = css`
  display: block;
  width: 100%;
  height: 100%;
  padding: 17px 0;
`;

const checkboxGroup = css`
  display: flex;
  gap: 20px;
  align-items: center;
`;

const inputStyle = css`
  display: flex;
  width: calc(100% - 40px);
  height: 36px;
  border-radius: 5px;
  border: 1px solid ${palette.gray3};
  padding: 0 10px;
  background-color: ${palette.white};
  position: relative;
  :focus {
    outline: none;
  }
  &[readOnly] {
    background-color: ${palette.gray1};
    color: ${palette.gray6};
  }
`;

const emailInputs = css`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;

  button {
    padding: 0;
    background-color: ${palette.colorDanger};
    &:hover,
    &active {
      background-color: ${palette.colorDanger};
    }
  }
`;

const investButton = css`
  display: flex;
  justify-content: flex-end;
  margin: 30px 0 20px 0;

  button {
    background-color: ${palette.colorDanger};
    &:hover,
    &active {
      background-color: ${palette.colorDanger};
    }
  }
`;
